import {now} from '@acng/frontend-bounty/timing/now.js';
import {ANY, ARRAY, BOOLEAN, INTEGER, NULLABLE, OBJECT, STRING} from '@acng/frontend-rubicon';
import {ARCHIMEDES_TRANSACTION_START_RESPONSE_DATA} from 'acng/payment/model/transaction.js';

/**
 * @typedef {Valid<typeof ARCHIMEDES_TRANSACTION_START_RESPONSE_DATA>} ArchiTxStartResponseData
 */

const METHOD_ID__SEPA = 1;

export class Method {

  /**
   * @param {ArchimedesMethodData} data
   */
  constructor(data) {
    this.id = data.id;
    this.verified_priority = data.verified_priority;
    this.legal_age_check = data.legal_age_check;
    this.icon = data.icon;
    this.name = data.name;
    this.description = data.description;
    this.verified = data.verified;
    this.resettable = data.resettable;
    this.country_restrictions = data.country_restrictions;
    this.sales_agent = data.sales_agent;
    this.request_consent_for_data_storage = data.request_consent_for_data_storage;
    this.raw_packages = data.packages;

    this.consent_for_data_storage = false;
    /**
     * @type {Record<string,any>}
     */
    this.packages = {};
    this.visible_percent_bonus = undefined;

    /**
     * @type {Array<ArchiTxStartResponseData>}
     */
    this.continuableTransactions = [];
  }

  preparePackages(packageList) {
    for (const actualCurrency in this.raw_packages) {
      this.packages[actualCurrency] = {};
      for (const packageId of this.raw_packages[actualCurrency]) {
        this.packages[actualCurrency][packageId] = true;

        const actualPackage = packageList.find((item) => item.id === packageId);
        if (actualPackage) {
          (actualPackage.methods || (actualPackage.methods = {}))[this.id] = true;
        }
      }
    }
  }

  /**
   * @param {Array<ArchiTxStartResponseData>} allContinuableTransactions
   */
  prepareContinuableTransactions(allContinuableTransactions) {
    this.continuableTransactions = allContinuableTransactions.filter(
      (tx) => tx.method_id === this.id
    );
  }

  cleanupExpiredContinuableTransactions() {
    this.continuableTransactions = this.continuableTransactions.filter(
      (tx) => tx.expires_at && (new Date(tx.expires_at)).getTime() > now()
    );
  }

  /**
   * @returns {ArchiTxStartResponseData|null}
   */
  getLastestContinuableTransaction() {
    this.cleanupExpiredContinuableTransactions();
    return this.continuableTransactions.find(() => true) || null;
  }

  /**
   * @returns {number}
   */
  getContinuableTransactionCount() {
    this.cleanupExpiredContinuableTransactions();
    return this.continuableTransactions.length;
  }

  /**
   * @returns {boolean}
   */
  hasContinuableTransactions() {
    if (this.id !== METHOD_ID__SEPA) {
      return false;
    }

    return this.getLastestContinuableTransaction() !== null;
  }
}

/**
 * @typedef {Valid<typeof ARCHIMEDES_METHOD_DATA>} ArchimedesMethodData
 */

const ARCHIMEDES_METHOD_DATA = /* @__PURE__ */ (() => OBJECT({
  id: INTEGER,
  verified_priority: NULLABLE(INTEGER),
  legal_age_check: BOOLEAN,
  icon: OBJECT({
    path: STRING
  }),
  name: STRING,
  description: STRING,
  verified: BOOLEAN,
  resettable: BOOLEAN,
  country_restrictions: ARRAY(STRING),
  sales_agent: NULLABLE(STRING),
  request_consent_for_data_storage: BOOLEAN,
  packages: ANY
}))();
